
import React from 'react';
import { AppUser, CampingSpot, GpxRoute, Coordinates, RouteStatus, RouteRegion, MapBounds, CollaborativeMap, AppNotification, ConditionTag, SpotVisit } from '../../types';
import type { SidebarMode } from '../../types/sidebar';
import { X } from 'lucide-react';
import { StickerBoard } from '../profile/StickerBoard';
import PeriodReplayControls, { usePeriodFilter } from '../profile/PeriodReplayControls';
import ReplayOverlay from '../profile/ReplayOverlay';
import NotificationsPanel from '../NotificationsPanel';
import AuthOverlay from '../AuthOverlay';
import ProfileOverlay from '../ProfileOverlay';
import CollaborativeMapModal from '../collaborative/CollaborativeMapModal';
import UpgradeOverlay from '../UpgradeOverlay';
import TrialExpiryBanner from '../TrialExpiryBanner';
import ReferralHandler from '../ReferralHandler';
import TesterApplicationModal from '../TesterApplicationModal';
import InstallAppModal from '../InstallAppModal';
import TesterInboxModal from '../TesterInboxModal';
import AddSpotModal, { AddSpotSubmitOptions } from '../AddSpotModal';
import SymbolLegend from '../SymbolLegend';
import LegalGuide from '../LegalGuide';
import TierGuide from '../gamification/TierGuide';
import WhatsNewModal from '../whatsnew/WhatsNewModal';
import AdminPanel from '../AdminPanel';
import MobileSpotSheet from '../MobileSpotSheet';
import AdminRouteUpload from '../AdminRouteUpload';

/**
 * Header-inngangen til reisetavla (eng-review D1: samme innhold som
 * Besøk-fanen) — datofilter + Reisefilm-knapp over klistremerketavla.
 * Egen komponent så usePeriodFilter-tilstanden lever/nullstilles med overlayet.
 */
const FilteredStickerBoard: React.FC<{ visits: SpotVisit[]; spots: CampingSpot[]; user: AppUser }> = ({ visits, spots, user }) => {
    const { filterFrom, setFilterFrom, filterTo, setFilterTo, hasPeriodFilter, periodVisits } = usePeriodFilter(visits);
    return (
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
            <PeriodReplayControls
                filterFrom={filterFrom}
                filterTo={filterTo}
                onFilterFromChange={setFilterFrom}
                onFilterToChange={setFilterTo}
                hasPeriodFilter={hasPeriodFilter}
                periodVisits={periodVisits}
            allVisits={visits}
                spots={spots}
                user={user}
            />
            <StickerBoard visits={periodVisits} spots={spots} user={user} />
        </div>
    );
};

interface AppOverlaysProps {
    isAuthOpen: boolean;
    setIsAuthOpen: (open: boolean) => void;
    isProfileOpen: boolean;
    setIsProfileOpen: (open: boolean) => void;
    currentUser: AppUser | null;
    showUpgrade: boolean;
    setShowUpgrade: (show: boolean) => void;
    vippsPaymentEnabled?: boolean;
    showTesterApplication: boolean;
    setShowTesterApplication: (show: boolean) => void;
    showAndroidGuide: boolean;
    setShowAndroidGuide: (show: boolean) => void;
    showInstallApp?: boolean;
    setShowInstallApp?: (show: boolean) => void;
    showFeedbackInbox: boolean;
    setShowFeedbackInbox: (show: boolean) => void;
    feedbackPrefill?: string;
    setFeedbackPrefill?: (text: string | undefined) => void;
    showAddModal: boolean;
    setShowAddModal: (show: boolean) => void;
    editingSpot?: CampingSpot;
    clearEditingSpot: () => void;
    pendingAnalysisCoords: Coordinates | null;
    handleAddSpotSubmit: (data: Partial<CampingSpot>, files: File[], options?: AddSpotSubmitOptions) => Promise<void>;
    initialFiles?: File[];
    clearInitialFiles?: () => void;
    showLegend: boolean;
    setShowLegend: (show: boolean) => void;
    showLegal: boolean;
    setShowLegal: (show: boolean) => void;
    showTierGuide: boolean;
    setShowTierGuide: (show: boolean) => void;
    showWhatsNew: boolean;
    setShowWhatsNew: (show: boolean) => void;
    showAdmin: boolean;
    setShowAdmin: (show: boolean) => void;
    mobileAdminInboxOnly?: boolean;
    adminTab: 'spots' | 'suggestions' | 'routes' | 'pending' | 'tools' | 'ai' | 'testers';
    setAdminTab: React.Dispatch<React.SetStateAction<'spots' | 'suggestions' | 'routes' | 'pending' | 'tools' | 'ai' | 'testers'>>;
    setMapCommand: (cmd: any) => void;
    handleSelectSpot: (spot: CampingSpot) => void;
    spots: CampingSpot[];
    removeSpotLocally: (id: string) => void;
    clearAlgorithmicSpots: () => Promise<void>;
    handleReevaluateViewport: () => Promise<any>;
    currentViewBounds?: MapBounds | null;
    selectedSpotId: string | null;
    setSelectedSpotId: (id: string | null) => void;
    showMobileSheet: boolean;
    setShowMobileSheet: (show: boolean) => void;
    canReturnToRoute?: boolean;
    onReturnToRoute?: () => void;
    canReturnToCollabMap?: boolean;
    onReturnToCollabMap?: () => void;
    mobileView: 'map' | 'list';
    favoriteSpotIds: string[];
    visitedSpotIds?: string[];
    collaborativeMaps?: CollaborativeMap[];
    onToggleFavoriteSpot: (e: React.MouseEvent, spotId: string) => void;
    onToggleVisit?: (e: React.MouseEvent, spotId: string) => void;
    onAddPastStay?: (spotId: string, checkInAt: number, checkedOutAt?: number) => Promise<unknown> | void;
    onDeletePastStay?: (spotId: string, checkInAt: number) => Promise<unknown> | void;
    onMarkOccupied?: (spotId: string, days: number, tags: ConditionTag[]) => void;
    onExtendOccupancy?: (spotId: string, days: number) => void;
    onClearOccupancy?: (spotId: string) => void;
    userPosition?: Coordinates | null;
    onAddToMap?: (e: React.MouseEvent, spotId: string) => void;
    showCollabMapModal?: boolean;
    setShowCollabMapModal?: (show: boolean) => void;
    activeCollabMapId?: string | null;
    setActiveCollabMapId?: (id: string | null) => void;
    addToMapSpotId?: string | null;
    setAddToMapSpotId?: (id: string | null) => void;
    addToMapRouteId?: string | null;
    setAddToMapRouteId?: (id: string | null) => void;
    routes?: GpxRoute[];
    isPaidUser?: boolean;
    reanalyzeSpot: (spot: CampingSpot) => void;
    activeAnalysisIds: Set<string>;
    handleEditSpot: (spot: CampingSpot) => void;
    generatingMetaIds?: Set<string>;
    showRouteUpload: boolean;
    setShowRouteUpload: (show: boolean) => void;
    editingRoute: GpxRoute | null;
    setEditingRoute: (route: GpxRoute | null) => void;
    preloadedRouteFile: File | null;
    setPreloadedRouteFile: (file: File | null) => void;
    preloadedRouteMetadata: any;
    setPreloadedRouteMetadata: (meta: any) => void;
    loadRoutesList: () => void;
    setPendingRouteId?: (routeId: string | null) => void;
    onRouteUploaded?: (routeId: string) => void;
    setSidebarMode: React.Dispatch<React.SetStateAction<SidebarMode>>;
    showNotifications?: boolean;
    setShowNotifications?: (v: boolean) => void;
    notifications?: AppNotification[];
    onMarkNotificationAsRead?: (notif: AppNotification) => void;
    onMarkAllNotificationsRead?: () => void;
    onSelectSpotFromMap?: (spot: CampingSpot, fromMapId: string) => void;
    onSelectRouteFromMap?: (routeId: string, fromMapId: string) => void;
    onGoToSpot?: (spotId: string, opts?: { verify?: boolean }) => void;
    testerApplicationsEnabled?: boolean;
    onNewUserRegistered?: () => void;
    visits: SpotVisit[];
    isStickerBoardOpen: boolean;
    setIsStickerBoardOpen: (open: boolean) => void;
    isReplayOpen?: boolean;
    setIsReplayOpen?: (open: boolean) => void;
}

import { useViewportCountry } from '../../hooks/useViewportCountry';

const AppOverlays: React.FC<AppOverlaysProps> = ({
    isAuthOpen, setIsAuthOpen,
    isProfileOpen, setIsProfileOpen,
    currentUser,
    showUpgrade, setShowUpgrade,
    vippsPaymentEnabled = false,
    showTesterApplication, setShowTesterApplication,
    showAndroidGuide, setShowAndroidGuide,
    showInstallApp, setShowInstallApp,
    showFeedbackInbox, setShowFeedbackInbox, feedbackPrefill, setFeedbackPrefill,
    showAddModal, setShowAddModal,
    editingSpot, clearEditingSpot,
    pendingAnalysisCoords,
    handleAddSpotSubmit,
    initialFiles, clearInitialFiles,
    showLegend, setShowLegend,
    showLegal, setShowLegal,
    showTierGuide, setShowTierGuide,
    showWhatsNew, setShowWhatsNew,
    showAdmin, setShowAdmin,
    mobileAdminInboxOnly = false,
    adminTab, setAdminTab,
    setMapCommand,
    handleSelectSpot,
    spots,
    removeSpotLocally,
    clearAlgorithmicSpots,
    handleReevaluateViewport,
    currentViewBounds,
    selectedSpotId, setSelectedSpotId,
    showMobileSheet, setShowMobileSheet,
    canReturnToRoute = false,
    onReturnToRoute,
    canReturnToCollabMap = false,
    onReturnToCollabMap,
    mobileView,
    favoriteSpotIds, visitedSpotIds, collaborativeMaps = [], onToggleFavoriteSpot, onToggleVisit, onAddPastStay, onDeletePastStay, onMarkOccupied, onExtendOccupancy, onClearOccupancy, userPosition, onAddToMap,
    showCollabMapModal, setShowCollabMapModal, activeCollabMapId, setActiveCollabMapId, addToMapSpotId, setAddToMapSpotId, addToMapRouteId, setAddToMapRouteId, routes = [], isPaidUser = false,
    reanalyzeSpot,
    activeAnalysisIds,
    handleEditSpot,
    generatingMetaIds,
    showRouteUpload, setShowRouteUpload,
    editingRoute, setEditingRoute,
    preloadedRouteFile, setPreloadedRouteFile,
    preloadedRouteMetadata, setPreloadedRouteMetadata,
    loadRoutesList,
    setPendingRouteId,
    onRouteUploaded,
    setSidebarMode,
    showNotifications, setShowNotifications,
    notifications = [], onMarkNotificationAsRead, onMarkAllNotificationsRead,
    onSelectSpotFromMap, onSelectRouteFromMap, onGoToSpot,
    testerApplicationsEnabled = false, onNewUserRegistered,
    visits, isStickerBoardOpen, setIsStickerBoardOpen,
    isReplayOpen = false, setIsReplayOpen,
}) => {
    const viewportCountryCode = useViewportCountry(currentViewBounds || null);
    // Admin: hvilken brukersamtale som er åpnet fra et "Brukermelding"-varsel
    const [adminInboxTesterUid, setAdminInboxTesterUid] = React.useState<string | null>(null);

    return (
        <>
            {isAuthOpen && <AuthOverlay onClose={() => setIsAuthOpen(false)} onRegistered={onNewUserRegistered} />}
            {isProfileOpen && currentUser && (
                <ProfileOverlay
                    user={currentUser}
                    onClose={() => setIsProfileOpen(false)}
                    onOpenUpgrade={() => setShowUpgrade(true)}
                    collaborativeMaps={collaborativeMaps}
                    isPaidUser={isPaidUser}
                    spots={spots}
                    testerApplicationsEnabled={testerApplicationsEnabled}
                    onOpenTesterApplication={() => { setIsProfileOpen(false); setShowTesterApplication(true); }}
                    onOpenInstallApp={() => { setIsProfileOpen(false); setShowInstallApp?.(true); }}
                    onOpenCollabMap={(id) => {
                        setIsProfileOpen(false);
                        setActiveCollabMapId?.(id);
                        setAddToMapSpotId?.(null);
                        setShowCollabMapModal?.(true);
                    }}
                    onFlyToSpot={(spotId, lat, lng) => {
                        setIsProfileOpen(false);
                        setMapCommand({ center: { latitude: lat, longitude: lng }, zoom: 15, id: Date.now() });
                        const spot = spots.find(s => s.id === spotId);
                        if (spot) handleSelectSpot(spot);
                    }}
                />
            )}

            {showNotifications && currentUser && (
                <NotificationsPanel
                    notifications={notifications}
                    currentUser={currentUser}
                    onClose={() => setShowNotifications?.(false)}
                    onMarkAsRead={(notif) => { onMarkNotificationAsRead?.(notif); }}
                    onMarkAllRead={() => { onMarkAllNotificationsRead?.(); }}
                    onOpenTesterInbox={() => {
                        setShowNotifications?.(false);
                        setShowFeedbackInbox(true);
                    }}
                    onOpenAdminTesterThread={(testerUid) => {
                        setShowNotifications?.(false);
                        setAdminInboxTesterUid(testerUid);
                    }}
                    onOpenAdminSuggestions={() => {
                        setShowNotifications?.(false);
                        setAdminTab('suggestions');
                        setShowAdmin(true);
                    }}
                    onOpenCollabMap={(mapId) => {
                        setShowNotifications?.(false);
                        setActiveCollabMapId?.(mapId);
                        setAddToMapSpotId?.(null);
                        setShowCollabMapModal?.(true);
                    }}
                    onGoToSpot={(spotId, opts) => {
                        setShowNotifications?.(false);
                        onGoToSpot?.(spotId, opts);
                    }}
                    onOpenRoute={(routeId) => {
                        setShowNotifications?.(false);
                        // Sørg for at rutelista lastes, så deep-link-effekten (pendingRouteId)
                        // finner ruten selv om brukeren aldri har åpnet rute-fanen.
                        loadRoutesList();
                        setPendingRouteId?.(routeId);
                    }}
                    onOpenUpgrade={() => {
                        setShowNotifications?.(false);
                        setShowUpgrade(true);
                    }}
                />
            )}

            {showUpgrade && currentUser && (
                <UpgradeOverlay
                    user={currentUser}
                    onClose={() => setShowUpgrade(false)}
                    vippsEnabled={vippsPaymentEnabled}
                    testerApplicationsEnabled={testerApplicationsEnabled}
                    onApplyTester={() => { setShowUpgrade(false); setShowTesterApplication(true); }}
                />
            )}

            {/* Prøveslutt-banner (#6A ærlig tapsramme): selv-gatende — vises kun til prøve-
                brukere nær slutt som ikke betaler, avvisbar for dagen. */}
            {currentUser && !showUpgrade && (
                <TrialExpiryBanner user={currentUser} onUpgrade={() => setShowUpgrade(true)} />
            )}

            {/* Verving siste-mil (#3): usynlig — fanger ?ref + claimer ved innlogging. */}
            <ReferralHandler user={currentUser} />
            {showTesterApplication && currentUser && <TesterApplicationModal user={currentUser} onClose={() => setShowTesterApplication(false)} />}
            {showAndroidGuide && currentUser && <TesterApplicationModal user={currentUser} onClose={() => setShowAndroidGuide(false)} androidGuideOnly />}
            {showInstallApp && <InstallAppModal user={currentUser} onClose={() => setShowInstallApp?.(false)} />}
            {showFeedbackInbox && currentUser && (
                <TesterInboxModal
                    currentUser={currentUser}
                    isAdmin={false}
                    initialMessage={feedbackPrefill}
                    initialCategory={feedbackPrefill ? 'improvement' : undefined}
                    onClose={() => { setShowFeedbackInbox(false); setFeedbackPrefill?.(undefined); }}
                />
            )}

            {adminInboxTesterUid && currentUser && (
                <TesterInboxModal
                    currentUser={currentUser}
                    isAdmin={true}
                    testerUid={adminInboxTesterUid}
                    onClose={() => setAdminInboxTesterUid(null)}
                />
            )}

            {showAddModal && pendingAnalysisCoords && (
                <AddSpotModal
                    coords={pendingAnalysisCoords}
                    initialData={editingSpot}
                    user={currentUser}
                    initialFiles={initialFiles}
                    onClose={() => {
                        setShowAddModal(false);
                        clearEditingSpot();
                        clearInitialFiles?.();
                    }}
                    onSubmit={handleAddSpotSubmit}
                />
            )}
            {showLegend && <SymbolLegend onClose={() => setShowLegend(false)} />}
            {showLegal && <LegalGuide onClose={() => setShowLegal(false)} initialCountry={viewportCountryCode} />}
            {showTierGuide && <TierGuide onClose={() => setShowTierGuide(false)} currentTierLevel={currentUser?.tier || 0} />}
            {showWhatsNew && <WhatsNewModal onClose={() => setShowWhatsNew(false)} />}

            {showAdmin && (
                <AdminPanel
                    initialTab={adminTab}
                    inboxOnly={mobileAdminInboxOnly}
                    currentUser={currentUser}
                    onClose={() => setShowAdmin(false)}
                    onViewSpot={(id, loc) => {
                        setMapCommand({ center: loc, zoom: 17, id: Date.now() });
                        setShowAdmin(false);
                        const spot = spots.find(s => s.id === id);
                        if (spot) handleSelectSpot(spot);
                    }}
                    onSpotRemoved={removeSpotLocally}
                    onOpenRouteUpload={() => { setShowAdmin(false); setShowRouteUpload(true); }}
                    onClearArea={clearAlgorithmicSpots}
                    onReevaluateArea={handleReevaluateViewport}
                    currentViewBounds={currentViewBounds}
                />
            )}

            {selectedSpotId && showMobileSheet && mobileView === 'map' && spots.find(s => s.id === selectedSpotId) && (
                <MobileSpotSheet
                    key={selectedSpotId}
                    spot={spots.find(s => s.id === selectedSpotId)!}
                    onClose={() => { setSelectedSpotId(null); setShowMobileSheet(false); }}
                    onExpand={() => { }}
                    showBackToRoute={canReturnToRoute}
                    onBackToRoute={onReturnToRoute}
                    showBackToCollabMap={canReturnToCollabMap}
                    onBackToCollabMap={onReturnToCollabMap}
                    isFavorite={favoriteSpotIds.includes(selectedSpotId)}
                    isVisited={visitedSpotIds?.includes(selectedSpotId)}
                    collaborativeMaps={collaborativeMaps}
                    onToggleFavorite={onToggleFavoriteSpot}
                    onToggleVisit={onToggleVisit}
                    onAddPastStay={onAddPastStay}
                    onDeletePastStay={onDeletePastStay}
                    onMarkOccupied={onMarkOccupied}
                    onExtendOccupancy={onExtendOccupancy}
                    onClearOccupancy={onClearOccupancy}
                    userPosition={userPosition}
                    onAddToMap={onAddToMap}
                    onReanalyze={reanalyzeSpot}
                    isAnalyzing={activeAnalysisIds.has(selectedSpotId)}
                    user={currentUser}
                    onRemoveSpot={removeSpotLocally}
                    onEdit={handleEditSpot}
                    onOpenAuth={() => setIsAuthOpen(true)}
                    isGeneratingMeta={selectedSpotId ? generatingMetaIds?.has(selectedSpotId) : false}
                />
            )}

            {showCollabMapModal && currentUser && (
                <CollaborativeMapModal
                    mapId={activeCollabMapId ?? null}
                    maps={collaborativeMaps}
                    spots={spots}
                    routes={routes}
                    currentUser={currentUser}
                    isPaidUser={isPaidUser}
                    addToMapSpotId={addToMapSpotId}
                    addToMapRouteId={addToMapRouteId}
                    listMode={!addToMapSpotId && !addToMapRouteId && !activeCollabMapId}
                    onClose={() => {
                        setShowCollabMapModal?.(false);
                        setAddToMapSpotId?.(null);
                        setAddToMapRouteId?.(null);
                    }}
                    onMapCreated={(id) => {
                        setActiveCollabMapId?.(id);
                        setShowCollabMapModal?.(true);
                    }}
                    onSelectSpot={(spot, fromMapId) => {
                        setShowCollabMapModal?.(false);
                        onSelectSpotFromMap?.(spot, fromMapId);
                    }}
                    onSelectRoute={(routeId, fromMapId) => {
                        setShowCollabMapModal?.(false);
                        onSelectRouteFromMap?.(routeId, fromMapId);
                    }}
                />
            )}

            {showRouteUpload && currentUser && (
                <AdminRouteUpload
                    key={editingRoute?.id || 'new-route'}
                    user={currentUser}
                    onClose={() => {
                        setShowRouteUpload(false);
                        setEditingRoute(null);
                        setPreloadedRouteFile(null);
                        setPreloadedRouteMetadata(undefined);
                    }}
                    onSuccess={(routeId) => {
                        loadRoutesList();
                        if (adminTab) setAdminTab('routes');
                        else setSidebarMode('routes');
                        setEditingRoute(null);
                        setPreloadedRouteFile(null);
                        setPreloadedRouteMetadata(undefined);
                        if (routeId) onRouteUploaded?.(routeId);
                    }}
                    editingRoute={editingRoute}
                    preloadedFile={preloadedRouteFile}
                    initialMetadata={preloadedRouteMetadata}
                />
            )}

            {isStickerBoardOpen && currentUser && (
                <div className="fixed inset-0 z-[10002] bg-black/70 backdrop-blur-md flex items-center justify-center p-3 md:p-6">
                    <div className="bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-panel w-full max-w-4xl h-[92vh] max-h-[92vh] overflow-hidden flex flex-col relative p-4 md:p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <button
                            onClick={() => setIsStickerBoardOpen(false)}
                            className="absolute top-4 right-4 md:top-5 md:right-5 p-2 rounded-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors z-[10003] border border-[var(--border-primary)]"
                        >
                            <X size={20} />
                        </button>
                        <FilteredStickerBoard
                            visits={visits}
                            spots={spots}
                            user={currentUser}
                        />
                    </div>
                </div>
            )}

            {/* Reisefilm åpnet direkte fra promo-kortet på hovedskjermen (ikke via
                profil-fanen). Overlayet henter selv checkin_log via getReplayVisits
                og lar veiviseren velge periode (siste tur som default). */}
            {isReplayOpen && currentUser && (
                <ReplayOverlay
                    visits={visits}
                    initialFrom=""
                    initialTo=""
                    spots={spots}
                    user={currentUser}
                    onClose={() => setIsReplayOpen?.(false)}
                />
            )}
        </>
    );
};

export default AppOverlays;
